import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SITE_NAME, SITE_TAGLINE } from "@/lib/site";

const navItems = [
  { label: "首页", to: "/" },
  { label: "话题档案", to: "/topics" },
  { label: "常见问题", to: "/faq" },
  { label: "关于我们", to: "/about" },
];

function todayLabel() {
  const now = new Date();
  const week = ["日", "一", "二", "三", "四", "五", "六"][now.getDay()];
  return `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日 周${week}`;
}

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const isActive = (to: string) => {
    if (to === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(to);
  };

  const runSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/topics?q=${encodeURIComponent(value)}` : "/topics");
    setMenuOpen(false);
  };

  return (
    <header>
      <div className="hidden bg-foreground-950 text-background-50 md:block">
        <div className="flex items-center justify-between px-4 py-2 text-xs md:px-8">
          <p className="flex items-center gap-2">
            <i className="ri-radar-line text-primary-300" aria-hidden="true" />
            当前追踪中话题 68 个 · 今日新增进展节点 12 条
          </p>
          <p className="flex items-center gap-3 text-background-100/80">
            <span>{todayLabel()}</span>
            <span className="h-3 w-px bg-background-50/25" aria-hidden="true" />
            <span>只理时间线，不替你做判断</span>
          </p>
        </div>
      </div>

      <div className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur-sm">
        <div className="flex h-16 items-center justify-between gap-4 px-4 md:h-[72px] md:px-8">
          <Link to="/" className="flex items-center gap-3" aria-label={`${SITE_NAME} 首页`}>
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-radar-line text-xl" aria-hidden="true" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-bold tracking-wide text-foreground-950">
                {SITE_NAME}
              </span>
              <span className="mt-1 hidden text-[11px] font-label text-foreground-500 sm:block">
                {SITE_TAGLINE}
              </span>
            </span>
          </Link>

          <nav aria-label="主导航" className="hidden md:flex md:items-center md:gap-1">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive(item.to)
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={runSearch}
              role="search"
              className="hidden lg:flex lg:items-center lg:gap-2 lg:rounded-md lg:border lg:border-background-300 lg:bg-background-100 lg:px-3 lg:py-2"
            >
              <i className="ri-search-line text-base text-foreground-500" aria-hidden="true" />
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索事件或标签"
                aria-label="搜索事件或标签"
                className="w-40 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none xl:w-52"
              />
            </form>

            <Link
              to="/#subscribe"
              className="hidden whitespace-nowrap rounded-md bg-foreground-950 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-foreground-800 sm:inline-flex"
            >
              订阅更新
            </Link>

            <Link
              to="/topics"
              aria-label="前往话题档案搜索"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 lg:hidden"
            >
              <i className="ri-search-line" aria-hidden="true" />
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label={menuOpen ? "关闭导航菜单" : "打开导航菜单"}
              aria-expanded={menuOpen}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 md:hidden"
            >
              <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-line text-lg"} aria-hidden="true" />
            </button>
          </div>
        </div>

        {menuOpen ? (
          <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 md:hidden">
            <form
              onSubmit={runSearch}
              role="search"
              className="mb-3 flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2"
            >
              <i className="ri-search-line text-base text-foreground-500" aria-hidden="true" />
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索事件或标签"
                aria-label="搜索事件或标签"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
              />
            </form>
            <nav aria-label="移动端导航" className="flex flex-col">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`whitespace-nowrap rounded-md px-3 py-3 text-sm font-medium transition-colors ${
                    isActive(item.to)
                      ? "bg-primary-50 text-primary-700"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <Link
              to="/#subscribe"
              className="mt-3 flex items-center justify-center whitespace-nowrap rounded-md bg-foreground-950 px-4 py-3 text-sm font-medium text-background-50"
            >
              订阅更新提醒
            </Link>
          </div>
        ) : null}
      </div>
    </header>
  );
}